import { Fragment, memo, useMemo, type ComponentType, type FC } from "react";
import dayjs from "dayjs";
import { Ban, CheckCheck, CircleCheck, CircleX, Clock, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MESSAGE_TIME_FORMAT, WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppTemplateStatus } from "../WhatsAppCampaignTypes";
import { splitTemplateBody } from "../WhatsAppCampaignUtils";

const STATUS_ICONS: Record<WhatsAppTemplateStatus, ComponentType<{ className?: string }>> = {
  approved: CircleCheck,
  pending: Clock,
  rejected: CircleX,
  unavailable: Lock,
};

interface TemplateStatusBadgeProps {
  status: WhatsAppTemplateStatus;
}

const StatusBadge: FC<TemplateStatusBadgeProps> = ({ status }) => {
  const { t } = useTranslation();
  const Icon = STATUS_ICONS[status];

  return (
    <span className={`wa-status-badge wa-status-badge--${status}`}>
      <Icon className="wa-status-badge__icon" aria-hidden />
      {t(`${WHATSAPP_EDITOR_KEY}.status.${status}`)}
    </span>
  );
};

export const TemplateStatusBadge = memo(StatusBadge);

interface WhatsAppMessageTextProps {
  body: string;
  className?: string;
}

const MessageText: FC<WhatsAppMessageTextProps> = ({ body, className = "" }) => {
  const segments = useMemo(() => splitTemplateBody(body), [body]);

  return (
    <p className={`wa-message-text ${className}`}>
      {segments.map((segment, position) =>
        segment.type === "text" ? (
          <Fragment key={position}>{segment.value}</Fragment>
        ) : (
          <strong key={position} className="wa-message-text__parameter">
            {`{{${segment.index}}}`}
          </strong>
        )
      )}
    </p>
  );
};

export const WhatsAppMessageText = memo(MessageText);

interface MessageMetaProps {
  status?: WhatsAppTemplateStatus;
}

const Meta: FC<MessageMetaProps> = ({ status = "approved" }) => {
  const { t } = useTranslation();

  if (status === "pending" || status === "rejected") {
    const Icon = status === "pending" ? Clock : Ban;
    return (
      <span className={`wa-message-meta wa-message-meta--${status}`}>
        {t(`${WHATSAPP_EDITOR_KEY}.status.${status}`)}
        <Icon className="wa-message-meta__icon" aria-hidden />
      </span>
    );
  }

  return (
    <span className="wa-message-meta">
      {dayjs().format(MESSAGE_TIME_FORMAT)}
      <CheckCheck className="wa-message-meta__icon wa-message-meta__icon--read" aria-hidden />
    </span>
  );
};

export const MessageMeta = memo(Meta);
